import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import type { CreatedTicket } from './NoteCreateModals';
import { useOpenJoinTableMutation, type WaiterTable } from './waiterApi';
import { MAX_TABLES_PER_SESSION } from './tableState';
import { formatTicketTime } from './ticketTime';

interface Props {
  /** `null` closes the modal — same "controlled by presence" shape as
   *  `NotesListPage`'s other one-target-at-a-time modals. */
  table: WaiterTable | null;
  token: string;
  onClose: () => void;
  onCreated: (ticket: CreatedTicket) => void;
  /** SC-966 — « Installer » a booking: the stepper starts on the party size
   *  (clamped to the table) instead of 2, and the waiter still adjusts it. */
  initialCovers?: number;
  /** One line under the title — who this table is being opened for. */
  subtitle?: string;
  submitLabel?: string;
  /** SC-976 — design §5.2 D5/D12: free tables the collapsed "+ Ajouter des
   *  tables" disclosure can offer, already scoped to the current salle by
   *  the caller. Empty (or absent) ⇒ the disclosure renders nothing — a
   *  plain "Ouvrir" tap stays exactly what it was (D12). */
  extraCandidates?: WaiterTable[];
}

/** D5 — « papier 58 mm en grande taille » ceiling: the anchor plus 5 more.
 *  Derived from the single shared `MAX_TABLES_PER_SESSION` (review P2#5) —
 *  `NotesListPage`'s `TOO_MANY_TABLES` message uses the same constant. */
const MAX_EXTRA_TABLES = MAX_TABLES_PER_SESSION - 1;

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.2 D3 — tapping a FREE tile
 * opens this centred modal; the only way `tables/<uuid>/session/` (open-or-
 * join, a real Salle state change) is ever called from the grid. Mirrors
 * `NoteCreateModals`'s `CreateTableModalBody` submit path exactly
 * (`setTicket` + navigate lives in the caller via `onCreated`).
 */
export default function OpenTableModal({
  table,
  token,
  onClose,
  onCreated,
  initialCovers,
  subtitle,
  submitLabel,
  extraCandidates,
}: Props) {
  const { t } = useTranslation();
  const open = table !== null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel={table ? t('waiter.notes.salle.openTitle', { number: table.number }) : ''}
      instant
    >
      {table && (
        <OpenTableModalBody
          // Remount on a new target so the stepper re-seeds from that
          // booking's party size rather than keeping the previous count.
          key={`${table.uuid}:${initialCovers ?? ''}`}
          table={table}
          token={token}
          onClose={onClose}
          onCreated={onCreated}
          initialCovers={initialCovers}
          subtitle={subtitle}
          submitLabel={submitLabel}
          extraCandidates={extraCandidates ?? []}
        />
      )}
    </Modal>
  );
}

interface BodyProps {
  table: WaiterTable;
  token: string;
  onClose: () => void;
  onCreated: (ticket: CreatedTicket) => void;
  initialCovers?: number;
  subtitle?: string;
  submitLabel?: string;
  extraCandidates: WaiterTable[];
}

function OpenTableModalBody({
  table,
  token,
  onClose,
  onCreated,
  initialCovers,
  subtitle,
  submitLabel,
  extraCandidates,
}: BodyProps) {
  const { t } = useTranslation();
  const coversMax = Math.max(1, table.maxCovers);
  const [covers, setCovers] = useState(() => Math.max(1, Math.min(initialCovers ?? 2, coversMax)));
  const [error, setError] = useState<string | null>(null);
  const [openJoinTable, { isLoading }] = useOpenJoinTableMutation();
  // SC-976 — design §5.2: the disclosure starts collapsed and empty every
  // time (D12 — a plain "Ouvrir" tap never touches this). No selection
  // persists across the remount `key` above either.
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraUuids, setExtraUuids] = useState<string[]>([]);
  // Review P2#7 — `extraCandidates` is the live poll's free-table list; if a
  // picked table leaves it (another device attached/occupied it while this
  // modal stayed open) prune its uuid here so `mergedLabel` and the outgoing
  // `extra_table_uuids` body never diverge — one source of truth, instead of
  // filtering `extraCandidates` for the label while sending the raw,
  // possibly-stale `extraUuids` to the server. Bails out (same array
  // reference) when nothing changed, so this never loops against the new
  // `extraCandidates` array identity the caller recomputes every render.
  useEffect(() => {
    setExtraUuids((prev) => {
      const next = prev.filter((uuid) => extraCandidates.some((c) => c.uuid === uuid));
      return next.length === prev.length ? prev : next;
    });
  }, [extraCandidates]);
  const selectedExtras = extraCandidates.filter((c) => extraUuids.includes(c.uuid));
  const mergedLabel = [table.number, ...selectedExtras.map((c) => c.number)].join('+');

  const handleSubmit = async () => {
    setError(null);
    try {
      const result = await openJoinTable({
        tableUuid: table.uuid,
        token,
        covers,
        extraTableUuids: extraUuids.length > 0 ? extraUuids : undefined,
      }).unwrap();
      onCreated({
        sessionUuid: result.session.uuid,
        kind: 'dine_in',
        label: result.session.tableLabel ?? String(table.number),
        covers: result.session.covers,
        openedAt: result.session.openedAt,
        guestToken: result.guestToken,
        isMerged: result.session.isMerged,
      });
    } catch (err) {
      const apiErr = err as { data?: { code?: string; max_covers?: number; table_number?: string | number } };
      if (apiErr.data?.code === 'covers_exceeds_capacity') {
        setError(t('waiter.notes.errors.coversExceedsCapacity', { n: apiErr.data.max_covers ?? coversMax }));
      } else if (apiErr.data?.code === 'TABLE_OCCUPIED') {
        setError(t('waiter.notes.errors.extraTableOccupied', { number: apiErr.data.table_number ?? '' }));
      } else if (apiErr.data?.code === 'TOO_MANY_TABLES') {
        setError(t('waiter.notes.errors.tooManyTables', { max: MAX_TABLES_PER_SESSION }));
      } else {
        setError(t('waiter.notes.errors.generic'));
      }
    }
  };

  return (
    <div
      data-testid="open-table-modal"
      // Review fix-T3-c P2#2 (Codex) — same fix as `NotesListPage`'s
      // `transfer-picker`: `Modal` clips at its own max-height
      // (`overflow-hidden`, by design) and disables body scroll while
      // open. The "+ Ajouter des tables" disclosure can add a whole grid
      // of chips on top of the covers stepper; on a short screen that
      // pushed Ouvrir/Annuler past the bottom, unreachable. `min-h-0`
      // lets this flex child shrink so `overflow-y-auto` can scroll it.
      className="p-5 flex flex-col gap-4 md:max-w-[400px] md:mx-auto md:w-full min-h-0 overflow-y-auto"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-sans text-[18px] font-bold">{t('waiter.notes.salle.openTitle', { number: table.number })}</h2>
        <button
          type="button"
          data-testid="open-close"
          aria-label={t('common.close')}
          onClick={onClose}
          className="-me-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
        >
          ×
        </button>
      </div>
      {subtitle && (
        <p data-testid="open-subtitle" className="font-sans text-base text-ink-mute -mt-2 break-words">
          {subtitle}
        </p>
      )}
      {/* DF-0349 — la table vient de partir en caisse. La rouvrir pour tout
          ressaisir mettait un second ticket dans la caisse (Yuji, 2026-10-01,
          tables 23 et S1) : c'est dit ici, au moment même du geste. Un liseré
          de début et un fond teinté, pas un cadre : cadre bleu + texte bleu,
          c'est la recette des boutons à contour du carnet. */}
      {table.lastCaisseHandoff && (
        <p
          data-testid="open-caisse-trace"
          className="border-s-4 px-3 py-2 font-sans text-base font-bold break-words"
          style={{
            borderColor: 'var(--w-info)',
            color: 'var(--w-info)',
            backgroundColor: 'color-mix(in srgb, var(--w-info) 8%, #fff)',
          }}
        >
          {table.lastCaisseHandoff.by
            ? t('waiter.notes.card.caisseTraceAtBy', {
                time: formatTicketTime(table.lastCaisseHandoff.at),
                name: table.lastCaisseHandoff.by,
              })
            : t('waiter.notes.card.caisseTraceAt', { time: formatTicketTime(table.lastCaisseHandoff.at) })}
        </p>
      )}
      <div className="flex items-center justify-between gap-3 font-mono text-sm">
        <span>{t('waiter.notes.createTable.covers')}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="open-covers-minus"
            aria-label="diminuer"
            disabled={covers <= 1}
            onClick={() => setCovers((c) => Math.max(1, c - 1))}
            className="w-12 h-12 border-2 border-ink rounded-btn-sm font-sans text-xl bg-paper text-ink disabled:opacity-40"
          >
            −
          </button>
          <span data-testid="open-covers-value" className="font-mono font-bold text-[20px] min-w-[2ch] text-center">
            {covers}
          </span>
          <button
            type="button"
            data-testid="open-covers-plus"
            aria-label="augmenter"
            disabled={covers >= coversMax}
            onClick={() => setCovers((c) => Math.min(coversMax, c + 1))}
            className="w-12 h-12 border-2 border-ink rounded-btn-sm bg-paper text-ink font-sans text-xl disabled:opacity-40"
          >
            +
          </button>
        </div>
      </div>
      {/* SC-976 — design §5.2 D5/D12: collapsed by default, renders nothing
          when the caller has no free table to offer (nowhere to onboard a
          restaurant with a single table left). A plain "Ouvrir" tap never
          opens this — it costs nothing to the habitué. */}
      {extraCandidates.length > 0 && (
        <div className="border-t-2 border-hairline pt-3 flex flex-col gap-2">
          {/* Same bordered h-11 disclosure chip as `NotesListPage`'s waiter
              switcher: as a bare bold line this read as a caption, not as a
              tap target, and the option went unnoticed. */}
          <button
            type="button"
            data-testid="open-extra-toggle"
            aria-expanded={extraOpen}
            onClick={() => setExtraOpen((v) => !v)}
            className="h-11 w-full flex items-center justify-between gap-2 rounded-btn-sm border-2 border-ink bg-paper px-3 font-sans text-base font-bold text-ink text-start"
          >
            <span>{t('waiter.notes.salle.openExtraToggle')}</span>
            <span aria-hidden="true" className={`text-xs ${extraOpen ? 'rotate-180' : ''}`}>
              ▾
            </span>
          </button>
          {extraOpen && (
            <>
              <p className="font-sans text-sm text-ink-mute">
                {t('waiter.notes.salle.openExtraPickLabel', { max: MAX_EXTRA_TABLES })}
              </p>
              <div
                data-testid="open-extra-grid"
                className="grid grid-cols-[repeat(auto-fill,minmax(56px,1fr))] gap-2"
              >
                {extraCandidates.map((extra) => {
                  const selected = extraUuids.includes(extra.uuid);
                  const disabled = !selected && extraUuids.length >= MAX_EXTRA_TABLES;
                  return (
                    <button
                      key={extra.uuid}
                      type="button"
                      data-testid={`open-extra-${extra.uuid}`}
                      aria-pressed={selected}
                      disabled={disabled}
                      onClick={() =>
                        setExtraUuids((prev) =>
                          selected ? prev.filter((u) => u !== extra.uuid) : [...prev, extra.uuid],
                        )
                      }
                      className={`h-11 rounded-btn-sm border-2 px-2 font-sans text-base font-bold truncate ${
                        selected ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink'
                      } disabled:opacity-40`}
                    >
                      {extra.number}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      {error && (
        <p data-testid="open-error" role="alert" className="font-mono text-sm text-red-600">
          {error}
        </p>
      )}
      {/* `min-h-12`, not `h-12`: with five extra tables the merged label
          (« Ouvrir 8+10+11+12+13+14 · 2 couverts ») needs two lines at
          390px — measured 67px (fr/en/ar) and 75px (zh) inside a 48px box,
          spilling over the row. Both buttons stretch together. */}
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="open-cancel"
          onClick={onClose}
          className="flex-1 min-h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          data-testid="open-submit"
          disabled={isLoading}
          aria-busy={isLoading}
          onClick={() => void handleSubmit()}
          className="flex-1 min-h-12 bg-ink text-paper rounded-btn px-4 py-2 font-sans text-[18px] font-bold leading-tight disabled:opacity-50"
        >
          {submitLabel ??
            (extraUuids.length > 0
              ? t('waiter.notes.salle.openSubmitMerged', { label: mergedLabel, n: covers })
              : t('waiter.notes.salle.openSubmit'))}
        </button>
      </div>
    </div>
  );
}
